import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import type { secondaryContentItemContent$key } from "~/queries/__generated__/secondaryContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  TopicLinkContainer,
} from "./styles";

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: secondaryContentItemContent$key;
};

export const ContentItemSecondary = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference: reference_ }, reference) => {
    const entity = useFragment(
      graphql`
        fragment secondaryContentItemContent on Content {
          ...entityLink
        }
      `,
      reference_,
    );

    const header = (
      <>
        <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
        <TopicLinkContainer>{provider.topicLink?.()}</TopicLinkContainer>
        <StyledHeadlineEntityLink reference={entity}>
          {provider.headline({
            hideTooltip: true,
            preferSocialHeadline: true,
          })}
        </StyledHeadlineEntityLink>
      </>
    );
    const cover = (
      <StyledCoverEntityLink reference={entity}>
        {provider.coverImage({
          responsiveVariants: {
            desktopUp: "size540x360",
            mobileUp: "size80x80",
            tabletUp: "size540x360",
          },
        })}
        {provider.video?.durationOverlay()}
      </StyledCoverEntityLink>
    );
    const content = provider.video || provider.sponsorTag ? null : provider.summary();
    const footer =
      provider.sponsorTag?.({
        color: "#001246",
        gap: "0px",
        layoutDirection: "vertical",
        nameFontSize: "12px",
        typeFontSize: "12px",
      }) ?? provider.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Cover>{cover}</Cover>
        <Content>{content}</Content>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemSecondary.displayName = "ContentItemSecondary";
